import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Input, SettingsPanel, Spinner } from "../../shared";
import { ClientsDropdown } from "../../components";
import { SettingsHeader } from ".";
import {
  useDisconnectGoogleDriveMutation,
  useGetGoogleDriveStatusQuery,
  useLazyConnectGoogleDriveQuery,
  useLazyGetGoogleDriveFoldersQuery,
  useUpdateGoogleDriveMutation,
} from "../../redux/services/google-drive/googleDriveApi";

/**
 * Settings page for linking a Google Drive folder.
 * New xlsx files in that folder become jobs.
 */
const GoogleDriveSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, refetch } = useGetGoogleDriveStatusQuery();
  const [loadFolders, foldersState] = useLazyGetGoogleDriveFoldersQuery();
  const [startConnect, connectState] = useLazyConnectGoogleDriveQuery();
  const [updateDrive, updateState] = useUpdateGoogleDriveMutation();
  const [disconnectDrive, disconnectState] = useDisconnectGoogleDriveMutation();

  const connection = data?.data;
  const [folderId, setFolderId] = useState("");
  const [folderQuery, setFolderQuery] = useState("");
  const [clientForm, setClientForm] = useState({ client: null });

  useEffect(() => {
    const result = searchParams.get("googleDrive");
    if (!result) return;
    if (result === "connected") toast.success("Google Drive connected");
    if (result === "denied") toast.warning("Google Drive connection was cancelled");
    if (result === "error") toast.error("Google Drive connection failed");
    searchParams.delete("googleDrive");
    setSearchParams(searchParams, { replace: true });
    refetch();
  }, [searchParams, setSearchParams, refetch]);

  useEffect(() => {
    if (!connection) return;
    setFolderId(connection.folderId || "");
    if (connection.defaultClient?._id) {
      setClientForm({ client: connection.defaultClient });
    }
  }, [connection?.folderId, connection?.defaultClient?._id]);

  useEffect(() => {
    if (connection?.connected) {
      loadFolders();
    }
  }, [connection?.connected, loadFolders]);

  const folders = foldersState.data?.data || [];
  const visibleFolders = useMemo(() => {
    const query = folderQuery.trim().toLowerCase();
    if (!query) return folders;
    return folders.filter((folder) =>
      String(folder.name || "").toLowerCase().includes(query)
    );
  }, [folders, folderQuery]);

  async function handleConnect() {
    if (!connection?.configured) {
      const missing = connection?.missingConfig?.join(", ");
      toast.error(
        missing
          ? `Set ${missing} on the API and restart it before connecting.`
          : "Google Drive is not configured on the API."
      );
      return;
    }
    try {
      const result = await startConnect().unwrap();
      if (result?.data?.url) {
        window.location.assign(result.data.url);
      }
    } catch (error) {
      toast.error(
        error?.data?.message ||
          error?.message ||
          "Unable to start Google sign-in"
      );
    }
  }

  async function handleSave() {
    const selected = folders.find((folder) => folder.id === folderId);
    if (!folderId || !clientForm.client?._id) {
      toast.error("Choose a folder and a default client");
      return;
    }
    try {
      await updateDrive({
        folderId,
        folderName: selected?.name || connection?.folderName,
        defaultClient: clientForm.client._id,
      }).unwrap();
      toast.success("Google Drive folder is being watched");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to save Google Drive settings");
    }
  }

  async function handleDisconnect() {
    try {
      await disconnectDrive().unwrap();
      setFolderId("");
      setClientForm({ client: null });
      toast.success("Google Drive disconnected");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to disconnect Google Drive");
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SettingsHeader
        title="Google Drive"
        actions={
          connection?.connected ? (
            <Button
              type="alternative"
              onClick={handleDisconnect}
              disabled={disconnectState.isLoading}
            >
              Disconnect
            </Button>
          ) : (
            <Button
              onClick={handleConnect}
              disabled={connectState.isFetching || connection?.configured === false}
            >
              Connect Google Drive
            </Button>
          )
        }
      />
      <SettingsPanel
        data={[
          {
            title: "Account",
            subtitle: connection?.configured
              ? "Attune watches one folder and creates a job when a new Excel file arrives."
              : "Connect stays off until the API has a Google OAuth client.",
            content: (
              <div className="text-sm text-slate-700 space-y-1">
                <div>
                  {connection?.connected
                    ? connection.email || "Connected"
                    : "Not connected"}
                </div>
                <div className="text-slate-500">
                  Status: {connection?.status || "disconnected"}
                </div>
                {connection?.missingConfig?.length > 0 && (
                  <div className="text-red-600">
                    Add these to the API environment, then restart the API:{" "}
                    {connection.missingConfig.join(", ")}
                  </div>
                )}
                {connection?.lastError && (
                  <div className="text-red-600">{connection.lastError}</div>
                )}
              </div>
            ),
          },
          connection?.connected
            ? {
                title: "Folder",
                subtitle: "Only new .xlsx files in this folder are imported.",
                content: (
                  <div className="space-y-2">
                    <Input
                      placeholder="Search folders"
                      value={folderQuery}
                      onChange={(target) => setFolderQuery(target?.value || "")}
                    />
                    <div className="max-h-48 overflow-auto border border-slate-200 rounded">
                      {foldersState.isFetching && (
                        <div className="p-3">
                          <Spinner size="sm" />
                        </div>
                      )}
                      {visibleFolders.map((folder) => (
                        <button
                          type="button"
                          key={folder.id}
                          className={`block w-full text-left px-3 py-2 text-sm ${
                            folderId === folder.id
                              ? "bg-primary-50 text-primary-800"
                              : "hover:bg-slate-50"
                          }`}
                          onClick={() => setFolderId(folder.id)}
                        >
                          {folder.name}
                        </button>
                      ))}
                      {!foldersState.isFetching && visibleFolders.length === 0 && (
                        <div className="p-3 text-sm text-slate-500">
                          No folders found
                        </div>
                      )}
                    </div>
                  </div>
                ),
              }
            : null,
          connection?.connected
            ? {
                title: "Default client",
                subtitle:
                  "Used when the workbook does not name a client in this organization.",
                content: (
                  <ClientsDropdown
                    form={clientForm}
                    value={clientForm.client}
                    hideFooter
                    onClick={(client) => setClientForm({ client })}
                    onChange={(client) => setClientForm({ client })}
                    updateForm={({ id, value }) => {
                      if (id === "client") setClientForm({ client: value });
                    }}
                  />
                ),
              }
            : null,
          connection?.connected
            ? {
                title: "Watch",
                subtitle: connection?.webhookConfigured
                  ? connection?.channelExpiration
                    ? `Channel renews before ${new Date(
                        connection.channelExpiration
                      ).toLocaleString()}`
                    : "Saving starts Drive notifications for the selected folder."
                  : "This server checks the folder about once a minute. A public webhook is only needed for instant notices.",
                content: (
                  <Button onClick={handleSave} disabled={updateState.isLoading}>
                    {updateState.isLoading ? "Saving..." : "Save and watch folder"}
                  </Button>
                ),
              }
            : null,
        ]}
      />
      {connection?.recentImports?.length > 0 && (
        <div className="bg-white rounded-xl p-4">
          <div className="font-semibold text-slate-800 mb-2">Recent files</div>
          <ul className="text-sm space-y-1">
            {connection.recentImports.map((item) => (
              <li key={`${item.fileName}-${item.date}`}>
                {item.fileName} — {item.status}
                {item.error ? ` (${item.error})` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default GoogleDriveSettings;
